import { Threads } from '@/components/ui/icons';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { LIMITS, renderMentionsAsText, type MessageDTO } from '@clanfire/shared';
import { threadApi } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useSubmit } from '@/hooks/useForm';
import { memberName, useServers } from '@/stores/servers';
import { threadPath, useThreads } from '@/stores/threads';
import { useUsers } from '@/stores/users';

/** A thread name suggested from a message: its first few words. */
function suggestName(message: MessageDTO): string {
  const users = useUsers.getState().users;
  const roles = useServers.getState().roles;
  const text = renderMentionsAsText(message.content, (id) => memberName(message.serverId, id) ?? users[id]?.displayName, (id) => roles[id]?.name)
    .replace(/<a?:(\w+):\w+>/g, ':$1:')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return message.attachments.length ? 'Attachment' : '';
  return text.length > 40 ? `${text.slice(0, 40).replace(/\s+\S*$/, '')}…` : text;
}

/** Start a thread on a message, or (with no message) a new one in the channel. */
export function CreateThreadModal({ channelId, message, onClose }: { channelId: string; message?: MessageDTO; onClose: () => void }) {
  const channelName = useServers((s) => s.channels[channelId]?.name ?? 'channel');
  const author = message ? (memberName(message.serverId, message.author?.id) ?? message.author?.displayName ?? 'someone') : null;
  const [name, setName] = useState(() => (message ? suggestName(message) : ''));
  const { loading, error, fields, run } = useSubmit();
  const navigate = useNavigate();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const thread = await run(() => threadApi.create(channelId, { name, ...(message ? { messageId: message.id } : {}) }));
    if (!thread) return;
    useThreads.getState().upsert([thread]);
    onClose();
    navigate(threadPath(thread));
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={message ? 'Start a thread' : `New thread in #${channelName}`}
      description={
        message
          ? `Talk about ${author}'s message on the side, without filling up #${channelName}.`
          : `A side conversation anyone who can see #${channelName} can join.`
      }
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="thread-form" loading={loading} icon={<Threads size={16} />}>
            Create thread
          </Button>
        </>
      }
    >
      <form id="thread-form" onSubmit={(e) => void submit(e)} className="flex flex-col gap-3">
        <Input
          label="Thread name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={LIMITS.threadNameMax}
          placeholder="What's it about?"
          error={fields.name}
          required
          autoFocus
          data-autofocus
        />
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
